'use client';

import { c as _c } from "react/compiler-runtime";
import { fluidRootCap } from '@/uiPrimitives/base/rootCap.css';
import { Dispatch, ReactNode, RefObject, SetStateAction, createContext, useContext, useEffect, useState } from 'react';
import { alphabet, AlphabetLetter } from './AlphabetAnchorLink.types';
import { produce } from '@/globals/helpers/immer';
const AlphabetizedAnchorInViewportContext = createContext<[Set<AlphabetLetter>, Dispatch<SetStateAction<Set<AlphabetLetter>>>] | undefined>(undefined);
const useAlphabetizedAnchorInViewportContext = () => {
  const context = useContext(AlphabetizedAnchorInViewportContext);
  if (context === undefined) {
    throw new Error("usesAlphabetizedAnchorInViewportContext must be used within a AlphabetizedAnchorInViewportContext");
  }
  return context;
};
export const useAnchorInViewport = () => {
  const $ = _c(2);
  const [anchorInViewport] = useAlphabetizedAnchorInViewportContext();
  let t0;
  if ($[0] !== anchorInViewport) {
    t0 = alphabet.find(letter => anchorInViewport.has(letter));
    $[0] = anchorInViewport;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const result = t0;
  return result;
};
const stickyBlockStartOffset = (() => {
  const menuBarSize = 1.2 ** 9 * fluidRootCap.max.px;
  const alphabetAnchorLinksBarSize = 1.2 ** 8 * fluidRootCap.max.px;
  return menuBarSize + alphabetAnchorLinksBarSize;
})();
export const useUpdateAnchorInViewport = t0 => {
  const $ = _c(5);
  const {
    letter,
    ref
  } = t0;
  const [, setAnchorInViewport] = useAlphabetizedAnchorInViewportContext();
  let t1;
  let t2;
  if ($[0] !== letter || $[1] !== ref || $[2] !== setAnchorInViewport) {
    t1 = () => {
      if (ref.current == null) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        setAnchorInViewport(produce(draft => {
          if (entries[0].isIntersecting) {
            draft.add(letter);
          } else {
            draft.delete(letter);
          }
        }));
      }, {
        threshold: 0,
        rootMargin: `-${stickyBlockStartOffset.toFixed(0)}px 0px 0px 0px`
      });
      observer.observe(ref.current);
      return () => {
        observer.disconnect();
        setAnchorInViewport(produce(draft_0 => {
          draft_0.delete(letter);
        }));
      };
    };
    t2 = [setAnchorInViewport, letter, ref];
    $[0] = letter;
    $[1] = ref;
    $[2] = setAnchorInViewport;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
};
export const AlphabetizedAnchorInViewportProvider = t0 => {
  const $ = _c(6);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [anchorInViewport, setAnchorInViewport] = useState(t1);
  let t2;
  if ($[1] !== anchorInViewport) {
    t2 = [anchorInViewport, setAnchorInViewport];
    $[1] = anchorInViewport;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <AlphabetizedAnchorInViewportContext.Provider value={t2}>{children}</AlphabetizedAnchorInViewportContext.Provider>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};